/**
 * Configuration types for the Data Notebook overlay panel
 */

export type Theme = 'light' | 'dark' | 'auto';
export type Position = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export type PanelState = 'minimized' | 'expanded' | 'hidden';
export type NamingStrategy = 'auto' | 'chrome-ai' | 'webllm' | 'manual';

/**
 * Interception configuration
 */
export interface InterceptionConfig {
  /** Enable network request interception (fetch/XHR) */
  network?: boolean;
  /** Enable client-side blob interception */
  blobs?: boolean;
  /** MIME types to intercept (default: CSV types) */
  mimeTypes?: string[];
  /** URL patterns to intercept (glob patterns) */
  urlPatterns?: string[];
  /** URL patterns to ignore */
  ignorePatterns?: string[];
}

/**
 * UI configuration
 */
export interface UIConfig {
  /** Theme mode */
  theme?: Theme;
  /** Initial panel position */
  position?: Position;
  /** Keyboard shortcut to toggle panel */
  keyboardShortcut?: string;
  /** Initial panel state */
  initialState?: PanelState;
  /** Show toast notifications for intercepted data */
  showToasts?: boolean;
}

/**
 * Database configuration
 */
export interface DatabaseConfig {
  /** Maximum memory usage (e.g., '512MB', '1GB') */
  memoryLimit?: string;
  /** Maximum number of tables to keep in memory */
  maxTables?: number;
  /** Query timeout in milliseconds */
  queryTimeout?: number;
  /** Enable query timing display */
  showQueryTiming?: boolean;
}

/**
 * Table naming configuration
 */
export interface NamingConfig {
  /** Naming strategy to use */
  strategy?: NamingStrategy;
  /** Enable automatic table naming */
  autoNaming?: boolean;
  /** Fallback table name prefix if naming fails */
  fallbackPrefix?: string;
}

/**
 * Security configuration
 */
export interface SecurityConfig {
  /** Allowed SQL operations (default: SELECT, DESCRIBE, SHOW, EXPLAIN) */
  allowedOperations?: string[];
  /** Maximum result rows to return */
  maxResultRows?: number;
  /** Enable CSP detection and warnings */
  cspDetection?: boolean;
}

/**
 * Complete configuration for Data Notebook
 */
export interface DataNotebookConfig {
  /** Interception settings */
  intercept?: InterceptionConfig;
  /** UI settings */
  ui?: UIConfig;
  /** Database settings */
  database?: DatabaseConfig;
  /** Table naming settings */
  naming?: NamingConfig;
  /** Security settings */
  security?: SecurityConfig;
}

/**
 * Default configuration values
 */
export const DEFAULT_CONFIG: Required<DataNotebookConfig> = {
  intercept: {
    network: true,
    blobs: true,
    mimeTypes: [
      'text/csv',
      'application/csv',
      'text/comma-separated-values',
      'application/vnd.ms-excel',
    ],
    urlPatterns: ['*'],
    ignorePatterns: [],
  },
  ui: {
    theme: 'auto',
    position: 'bottom-right',
    keyboardShortcut: 'Cmd+Shift+K',
    initialState: 'minimized',
    showToasts: true,
  },
  database: {
    memoryLimit: '512MB',
    maxTables: 50,
    queryTimeout: 30000,
    showQueryTiming: true,
  },
  naming: {
    strategy: 'auto',
    autoNaming: true,
    fallbackPrefix: 'table',
  },
  security: {
    allowedOperations: ['SELECT', 'DESCRIBE', 'SHOW', 'EXPLAIN', 'PRAGMA'],
    maxResultRows: 10000,
    cspDetection: true,
  },
};

/**
 * Merge user config with defaults
 */
export function mergeConfig(
  userConfig: DataNotebookConfig = {}
): Required<DataNotebookConfig> {
  return {
    intercept: { ...DEFAULT_CONFIG.intercept, ...userConfig.intercept },
    ui: { ...DEFAULT_CONFIG.ui, ...userConfig.ui },
    database: { ...DEFAULT_CONFIG.database, ...userConfig.database },
    naming: { ...DEFAULT_CONFIG.naming, ...userConfig.naming },
    security: { ...DEFAULT_CONFIG.security, ...userConfig.security },
  };
}
